import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
    APIProductCategory,
    APIProductManufacturer,
    APIProductModel,
} from '@visorpro/client'
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Cell,
} from 'material-react-table'
import type {
    APICreateModelRequest,
    APIUpdateModelRequest,
} from '@visorpro/client/dist/RestClient/services/ModelService'
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Dialog,
    Select,
    Stack,
    TextField,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    ChipProps,
    Chip,
} from '@mui/material'
import { Category } from '@mui/icons-material'
import { formatDate } from '../util/format'
import type { Stores } from '../util/store'
import { type SearchableSelectOption } from './searchable-select'
import { useAdminMaterialReactTable } from './table/mui-table'
import { useMultiValueColumnDef } from './table/column-definition'
import { createdByColumnDef } from './table/created-by-column'

export interface ModelChipProps extends ChipProps {
    name: string
    manufacturer: string
}

export const ModelChip = ({ name, manufacturer, ...props }: ModelChipProps) => {
    const label = (
        <>
            <span>{name}</span>
            <span style={{ fontWeight: 'normal' }}>
                {' '}
                ({manufacturer})
            </span>
        </>
    )
    return (
        <Chip
            size="small"
            label={label}
            title={`${name} / ${manufacturer}`}
            icon={<Category />}
            {...props}
        />
    )
}

export interface ModelsTableProps {
    models: APIProductModel[]
    stores: Stores
    manufacturers: APIProductManufacturer[]
    categories: APIProductCategory[]
    isLoading?: boolean
    showProgressBars?: boolean
    rowCount?: number
    isPrimaryBackgroundColor?: boolean
    createModel?: (body: APICreateModelRequest) => Promise<void>
    updateModel?: (
        modelId: string,
        body: Partial<APIUpdateModelRequest>,
    ) => Promise<void>
    deleteModel?: (modelId: string) => Promise<void>
    mergeModels?: (modelIds: string[]) => Promise<void>
}

export const ModelsTable = ({
    models,
    stores,
    manufacturers,
    categories,
    rowCount,
    isLoading,
    showProgressBars,
    isPrimaryBackgroundColor,
    createModel,
    updateModel,
    deleteModel,
    mergeModels,
}: ModelsTableProps) => {
    const [isCreatingModel, setIsCreatingModel] = useState(false)
    const [isMergingModels, setIsMergingModels] = useState(false)
    const [isDeletingModels, setIsDeletingModels] = useState(false)

    const [modelsMergedSnackbarOpen, setModelsMergedSnackbarOpen] =
        useState(false)
    const [modelsDeletedSnackbarOpen, setModelsDeletedSnackbarOpen] =
        useState(false)

    const onEditCell = useCallback(
        (
            cell: MRT_Cell<APIProductModel>,
            body:
                | Partial<APIUpdateModelRequest>
                | Partial<APICreateModelRequest>,
        ) => {
            const original = cell.row.original
            const manufacturer = manufacturers.find(
                (manufacturer) =>
                    manufacturer.id === body.product_manufacturer_id,
            )
            const category = categories.find(
                (category) => category.id === body.product_category_id,
            )
            const updated = {
                ...original,
                ...body,
                product_manufacturer: manufacturer,
                product_category: category,
            }
            Object.assign(cell.row.original, updated)
            void updateModel?.(cell.row.id, body)
        },
        [categories, manufacturers, updateModel],
    )

    const getManufacturerOptions = (
        manufacturers: APIProductManufacturer[],
    ): SearchableSelectOption[] => {
        return manufacturers.map((manufacturer) => ({
            value: manufacturer.id,
            label: manufacturer.name,
        }))
    }

    const manufacturerColumnDefs = useMultiValueColumnDef<
        APIProductModel,
        APIProductManufacturer
    >({
        fieldName: 'Manufacturer',
        optionsData: manufacturers,
        getOptions: getManufacturerOptions,
    })

    const getCategoryOptions = (
        categories: APIProductCategory[],
    ): SearchableSelectOption[] => {
        return categories.map((category) => ({
            value: category.id,
            label: category.name,
        }))
    }

    const categoryColumnDefs = useMultiValueColumnDef<
        APIProductModel,
        APIProductCategory
    >({
        fieldName: 'Category',
        optionsData: categories,
        getOptions: getCategoryOptions,
    })

    const columns = useMemo<MRT_ColumnDef<APIProductModel>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                enableClickToCopy: true,
                size: 400,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                size: 600,
                enableClickToCopy: true,
                enableEditing: updateModel !== undefined,
                muiEditTextFieldProps: ({ cell }) => ({
                    type: 'text',
                    required: true,
                    onBlur: (event) => {
                        onEditCell(cell, { name: event.target.value })
                    },
                }),
            },
            {
                accessorKey: 'product_manufacturer',
                header: 'Manufacturer',
                enableEditing: updateModel !== undefined,
                Cell: ({ cell }) => {
                    const manufacturer = cell.getValue<APIProductManufacturer>()
                    if (manufacturer === undefined) {
                        return ''
                    }
                    return manufacturer.name
                },
                Edit: ({ cell, table }) => {
                    const manufacturerId =
                        cell.getValue<APIProductManufacturer>().id
                    return (
                        <Select
                            fullWidth
                            defaultValue={manufacturerId}
                            size="small"
                            variant="standard"
                            onBlur={(event) => {
                                table.setEditingCell(null)
                                onEditCell(cell, {
                                    product_manufacturer_id: event.target.value,
                                })
                            }}
                            label="Manufacturer">
                            {manufacturers.map((manufacturer) => (
                                <MenuItem
                                    key={manufacturer.id}
                                    value={manufacturer.id}>
                                    {manufacturer.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )
                },
                filterFn: (row, _, filterValue: string[]) => {
                    const manufacturerId = row.original.product_manufacturer_id
                    return filterValue.includes(manufacturerId)
                },
                enableColumnFilter: manufacturerColumnDefs.enableColumnFilter,
                Filter: manufacturerColumnDefs.Filter,
            },
            {
                accessorKey: 'product_category',
                header: 'Category',
                enableEditing: updateModel !== undefined,
                Cell: ({ cell }) => {
                    const category = cell.getValue<APIProductCategory>()
                    if (category === undefined) {
                        return ''
                    }
                    return cell.getValue<APIProductCategory>().name
                },
                Edit: ({ cell, table }) => {
                    const categoryId = cell.getValue<APIProductCategory>().id

                    return (
                        <Select
                            fullWidth
                            defaultValue={categoryId}
                            label="Category"
                            size="small"
                            variant="standard"
                            onBlur={(event) => {
                                table.setEditingCell(null)
                                onEditCell(cell, {
                                    product_category_id: event.target.value,
                                })
                            }}>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )
                },
                filterFn: (row, _, filterValue: string[]) => {
                    const categoryId = row.original.product_category_id
                    return filterValue.includes(categoryId)
                },
                ...categoryColumnDefs,
            },
            {
                accessorFn: (doc) => doc.updated_at,
                header: 'Updated At',
                id: 'updated_at',
                Cell: ({ row }) => formatDate(row.getValue('updated_at')),
                filterVariant: 'datetime-range',
            },
            {
                accessorFn: (doc) => doc.created_at,
                header: 'Created At',
                id: 'created_at',
                Cell: ({ row }) => formatDate(row.getValue('created_at')),
                filterVariant: 'datetime-range',
            },
            createdByColumnDef(stores),
        ],
        [
            categories,
            categoryColumnDefs,
            manufacturerColumnDefs,
            manufacturers,
            onEditCell,
            stores,
            updateModel,
        ],
    )

    const table = useAdminMaterialReactTable(
        {
            columns,
            data: models,
            rowCount,
            enableRowSelection: true,
            enableSorting: true,
            initialState: {
                columnVisibility: {
                    id: false,
                },
            },
            defaultColumn: {
                enableColumnFilter: true,
                enableSorting: true,
            },
            getRowId: (doc) => doc.id,
            state: {
                isLoading,
                showProgressBars,
            },
            renderTopToolbarCustomActions: ({ table }) => {
                const onCreateModel = () => setIsCreatingModel(true)
                const onMergeModels = () => setIsMergingModels(true)
                const onDeleteModels = () => setIsDeletingModels(true)
                const selectedRows = table.getSelectedRowModel().flatRows

                return (
                    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                        {createModel && (
                            <Button onClick={onCreateModel}>
                                Create model
                            </Button>
                        )}
                        {mergeModels && (
                            <Button
                                onClick={onMergeModels}
                                disabled={selectedRows.length < 2}>
                                {selectedRows.length > 1
                                    ? `Merge ${selectedRows.length} models`
                                    : 'Merge models'}
                            </Button>
                        )}
                        {deleteModel && (
                            <Button
                                onClick={onDeleteModels}
                                disabled={selectedRows.length === 0}>
                                {selectedRows.length > 1
                                    ? `Delete ${selectedRows.length} models`
                                    : 'Delete model'}
                            </Button>
                        )}
                    </Box>
                )
            },
            createDisplayMode: 'custom',
            isPrimaryBackgroundColor,
        },
        'models-table',
    )

    const onMergeModels = async (modelIds: string[]) => {
        await mergeModels?.(modelIds)
        table.resetRowSelection()
        setModelsMergedSnackbarOpen(true)
        setIsMergingModels(false)
    }

    const onDeleteModels = async (modelIds: string[]) => {
        await Promise.allSettled(
            modelIds.map((modelId) => deleteModel?.(modelId)),
        )
        table.resetRowSelection()
        setModelsDeletedSnackbarOpen(true)
        setIsDeletingModels(false)
    }

    const onModelsMergedSnackbarClose = () => setModelsMergedSnackbarOpen(false)
    const onModelsDeletedSnackbarClose = () =>
        setModelsDeletedSnackbarOpen(false)

    return (
        <>
            <MaterialReactTable table={table} />
            <CreateModelDialog
                open={isCreatingModel}
                manufacturers={manufacturers}
                categories={categories}
                setOpen={setIsCreatingModel}
                createModel={createModel}
            />
            <MergeModelDialog
                open={isMergingModels}
                setOpen={setIsMergingModels}
                selectedModels={table
                    .getSelectedRowModel()
                    .flatRows.map((row) => row.original as APIProductModel)}
                mergeModels={onMergeModels}
            />
            <DeleteModelDialog
                open={isDeletingModels}
                setOpen={setIsDeletingModels}
                selectedModels={table
                    .getSelectedRowModel()
                    .flatRows.map((row) => row.original as APIProductModel)}
                deleteModels={onDeleteModels}
            />
            <Snackbar
                open={modelsMergedSnackbarOpen}
                autoHideDuration={2000}
                onClose={onModelsMergedSnackbarClose}
                message="Models merged successfully"
            />
            <Snackbar
                open={modelsDeletedSnackbarOpen}
                autoHideDuration={2000}
                onClose={onModelsDeletedSnackbarClose}
                message="Models deleted successfully"
            />
        </>
    )
}

interface CreateModelDialogProps {
    manufacturers: APIProductManufacturer[]
    categories: APIProductCategory[]
    open: boolean
    setOpen: (open: boolean) => void
    createModel?: (model: APICreateModelRequest) => void
}

const CreateModelDialog = ({
    manufacturers,
    categories,
    open,
    setOpen,
    createModel,
}: CreateModelDialogProps) => {
    const [name, setName] = useState<string>('')
    const [manufacturer, setManufacturer] = useState<string>('')
    const [category, setCategory] = useState<string>('')

    const onCreate = () => {
        createModel?.({
            name,
            product_manufacturer_id: manufacturer,
            product_category_id: category,
        })
        onClose()
    }
    const onClose = () => setOpen(false)

    useEffect(() => {
        if (open) {
            setName('')
            setManufacturer('')
            setCategory('')
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Create product model</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Stack spacing={1} sx={{ py: 1 }}>
                        <TextField
                            required
                            id="create-model-name"
                            label="Name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />

                        <FormControl fullWidth>
                            <InputLabel
                                id="create-model-manufacturer-label"
                                required>
                                Manufacturer
                            </InputLabel>
                            <Select
                                label="Manufacturer"
                                labelId="create-model-manufacturer-label"
                                value={manufacturer}
                                onChange={(event) =>
                                    setManufacturer(event.target.value)
                                }>
                                {manufacturers.map((manufacturer) => (
                                    <MenuItem
                                        key={manufacturer.id}
                                        value={manufacturer.id}>
                                        {manufacturer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel
                                id="create-model-category-label"
                                required>
                                Category
                            </InputLabel>
                            <Select
                                label="Manufacturer"
                                labelId="create-model-category-label"
                                value={category}
                                onChange={(event) =>
                                    setCategory(event.target.value)
                                }>
                                {categories.map((category) => (
                                    <MenuItem
                                        key={category.id}
                                        value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onCreate}
                    disabled={
                        name === '' || manufacturer === '' || category === ''
                    }>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface MergeModelDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    selectedModels: APIProductModel[]
    mergeModels: (modelIds: string[]) => void
}

const MergeModelDialog = ({
    open,
    setOpen,
    selectedModels,
    mergeModels,
}: MergeModelDialogProps) => {
    const onMerge = () => {
        mergeModels(selectedModels.map((model) => model.id))
        onClose()
    }
    const onClose = () => setOpen(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Merge {selectedModels.length} product models</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div>
                        <p>Are you sure you want to merge the following{' '}
                            {selectedModels.length} models?
                        </p>
                        <p>
                            The first model in the list will be kept, and all other
                            models will be merged into it.
                        </p>
                    </div>
                    <Box
                        sx={{
                            marginTop: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}>
                        {selectedModels.map((model, index) => (
                            <ModelChip
                                name={model.name}
                                manufacturer={
                                    model.product_manufacturer?.name || ''
                                }
                                key={index}
                            />
                        ))}
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={onMerge}>
                    Merge
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DeleteModelDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    selectedModels: APIProductModel[]
    deleteModels: (modelIds: string[]) => void
}

const DeleteModelDialog = ({
    open,
    setOpen,
    selectedModels,
    deleteModels,
}: DeleteModelDialogProps) => {
    const onDelete = () => {
        deleteModels(selectedModels.map((model) => model.id))
        onClose()
    }
    const onClose = () => setOpen(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Delete {selectedModels.length > 1 && selectedModels.length} product model{selectedModels.length !== 1 ? 's' : ''}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div>
                        Are you sure you want to delete the following{' '}
                        {selectedModels.length} models?
                    </div>
                    <Box
                        sx={{
                            marginTop: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}>
                        {selectedModels.map((model, index) => (
                            <ModelChip
                                name={model.name}
                                manufacturer={
                                    model.product_manufacturer?.name || ''
                                }
                                key={index}
                            />
                        ))}
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={onDelete}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    )
}
